import { type LineItemMessage } from "./common";
import { type LineItem } from "~/features/cart/cart/models/CartData";

type LineItemUpdate = Partial<LineItem> & Pick<LineItem, "messages">;

function findMessage(list: LineItemMessage[], key: string) {
  return list.find((item) => item.key === key);
}

// List of fields that can change in a Line Item, but should not trigger sending an update to hybris
const NON_UPDATABLE_FIELDS: (keyof LineItem)[] = [
  "pk",
  "priceOverriden",
  "messages",
  "contractErrorMessage",
  "minRdd",
  "maxRdd",
  "showComments",
  "isInStock",
  "entryNumber", // This field exists in hybris, but FE should not send updates about it
  "actualNetPrice",
  "actualSalePrice",
  "partialDeliveries",
  "edd",
  "netPrice",
  "tariffSurcharge",
];

export class LineItemBuilder {
  private _update: LineItemUpdate;
  private _messagesToRemove: string[] = [];
  private _productCode: string;

  constructor(
    private lineItem: LineItem,
    productCode: string,
  ) {
    this._update = { messages: [] };
    this._productCode = productCode;
  }

  set<T extends keyof LineItem>(key: T, value: LineItem[T]) {
    this._update[key] = value;
  }

  get<T extends keyof LineItem>(key: T): LineItem[T] {
    return (this._update[key] !== undefined ? this._update[key] : this.lineItem[key]) as LineItem[T];
  }

  addMessage(newMsg: LineItemMessage) {
    const index = this._messagesToRemove.indexOf(newMsg.key);
    if (index !== -1) {
      this._messagesToRemove.splice(index, 1);
    }
    if (!this.findMessage(newMsg.key)) {
      this._update.messages.push(newMsg);
    }
  }

  findMessage(messageKey: string, ignoreRemoval?: boolean) {
    return (
      findMessage(this._update.messages, messageKey) ||
      (!this._messagesToRemove.includes(messageKey) && findMessage(this.lineItem.messages, messageKey))
    );
  }

  removeMessage(messageKey: string) {
    let index = this._update.messages.findIndex((item) => item.key === messageKey);
    if (index !== -1) {
      this._update.messages.splice(index, 1);
    }

    index = this.lineItem.messages.findIndex((item) => item.key === messageKey);
    if (index !== -1) {
      this._messagesToRemove.push(messageKey);
    }
  }

  isFieldUpdated<T extends keyof LineItem>(key: T): boolean {
    return this._update[key] !== undefined;
  }

  _hasMessageUpdates(): boolean {
    return this._update.messages.length > 0 || this._messagesToRemove.length > 0;
  }

  hasUpdates() {
    const keys = Object.keys(this._update).filter((key) => key !== "messages");
    return keys.length > 0 || this._hasMessageUpdates();
  }

  hasUpdatesForSending() {
    const keys = Object.keys(this._update).filter((key) => NON_UPDATABLE_FIELDS.indexOf(key as keyof LineItem) === -1);
    return keys.length > 0;
  }

  getOriginal() {
    return this.lineItem;
  }

  forceBuild() {
    return {
      ...this.lineItem,
      ...this._update,
      messages: this._buildMessages(),
    };
  }

  _buildMessages() {
    return this.lineItem.messages
      .filter((msg) => !this._messagesToRemove.includes(msg.key))
      .concat(this._update.messages);
  }

  build() {
    if (this.hasUpdates()) {
      return {
        ...this.lineItem,
        ...this._update,
        messages: this._buildMessages(),
      };
    } else {
      return null;
    }
  }

  buildForSending(): LineItem | null {
    if (this.hasUpdatesForSending()) {
      return {
        ...this.lineItem,
        ...this._update,
        messages: this._buildMessages(),
        productCode: this._productCode,
      };
    } else {
      return null;
    }
  }

  getProductCode() {
    return this._productCode;
  }
}

export class ForcedLineItemBuilder extends LineItemBuilder {
  hasUpdatesForSending() {
    return true;
  }
}
